import { Form, Formik, type FormikHelpers } from "formik";
import { useState } from "react";
import { toast } from "sonner";
import { FormField } from "@/components/common/form-field";
import { brand } from "@/config/brand";
import { updateFirmSettings } from "@/features/onboarding/api";
import {
  type PortalTeamValues,
  portalTeamInitial,
  portalTeamSchema,
} from "@/features/onboarding/schema";
import { applyApiErrors } from "@/lib/form-errors";
import { CheckboxField } from "../onboarding-fields";
import { HubStepShell } from "../hub/hub-step-shell";
import type { OnboardingStepProps } from "./types";

/** Step 4 — enable + name the client portal. */
export function PortalStep({ meta, index, isFirst, onBack, onSkip, onSaved }: OnboardingStepProps) {
  const [submitting, setSubmitting] = useState(false);

  const save = async (values: PortalTeamValues, { setErrors }: FormikHelpers<PortalTeamValues>) => {
    setSubmitting(true);
    try {
      if (values.portalEnabled) {
        await updateFirmSettings({
          portalEnabled: true,
          portalName: values.portalName.trim(),
        });
      }
      onSaved();
    } catch (err) {
      toast.error(
        applyApiErrors(err, { setErrors }, { fallback: "Couldn't save the portal settings." }),
      );
      setSubmitting(false);
    }
  };

  return (
    <Formik initialValues={portalTeamInitial} validationSchema={portalTeamSchema} onSubmit={save}>
      {({ values }) => (
        <Form>
          <HubStepShell
            meta={meta}
            index={index}
            onBack={onBack}
            onSkip={onSkip}
            isFirst={isFirst}
            submitting={submitting}
          >
            <CheckboxField name="portalEnabled" label="Enable the client portal" />
            {values.portalEnabled && (
              <div className="flex flex-col gap-1">
                <FormField name="portalName" label="Portal name" placeholder="yourfirm" autoComplete="off" />
                <p className="text-muted-foreground text-xs">
                  Portal URL:{" "}
                  <span className="font-mono">
                    {brand.portalDomain}/{values.portalName.trim() || "yourfirm"}
                  </span>
                </p>
              </div>
            )}
          </HubStepShell>
        </Form>
      )}
    </Formik>
  );
}
